<template>
  <div>
    <div v-if="disabled" class="disabled-card" />
    <div v-if="titleCard" class="flex flex-space-between mb-10">
      <div class="text-h1">
        {{ titleCard }}
      </div>
      <a v-if="clearable" @click="emit('clear')" class="vz-cursor-pointer text-decoration-underline">
        Очистить
      </a>
    </div>
    <city-autocomplete
      ref="cityAutocomplete"
      :modelValue="locationGuid"
      :label="selectorTitle"
      :disabled="disabled || disableLocation"
      :search-options="computedLocationSearchOptions"
      :required="required"
      :rules="rules"
      :labelPublicStyle="publicStyle"
      placeholder="Введите текст для поиска"
      class="mb-10"
      :class="{'border-light': isFake}"
      show-region
      big
      @change="onChangeLocation"
    />

    <template v-if="!hideSwitcher && locationGuid">
      <vz-switcher
        v-if="!isRadioSwitcher"
        big
        class="mt-20 mb-20"
        :modelValue="direction"
        :items="directions"
        :is-load="isLoadingFullPrice"
        :disabled="disabled || isFake"
        :clearable="clearable"
        :class="{'tiny-switch-items': directions.length > 2}"
        @update:modelValue="onChangeDirection"
      />

      <vz-radiogroup
        v-else
        :modelValue="direction"
        :items="directions"
        :disabled="disabled || isFake"
        class="inline mt-15 mb-20"
        @update:modelValue="onChangeDirection"
      />
    </template>

    <slot name="switch-append" />

    <template v-if="direction && selectedLocation?.guid">
      <address-autocomplete
        v-if="isAddress"
        :modelValue="address"
        :location="selectedLocation"
        :restricted-items="restrictedItems"
        :disabled="disabled || isFake"
        :clearable="clearable"
        :clear-value-after-blur="addressClearValueAfterBlur"
        :required="required"
        :rules="rules"
        :use-map="useMap"
        :max-length="200"
        :suggestion-off="addressSuggestionOff"
        :terminal-purpose="terminalPurpose"
        :title="isFake ? 'Нет фиксированного расчёта цены' : ''"
        :labelPublicStyle="publicStyle"
        big
        show-switcher
        placeholder="Улица, дом"
        description-template="{region}"
        class="mb-20"
        @map-submit="onMapSubmit"
        @change="onChangeAddress"
        @update:modelValue="setAddress"
      />

      <terminal-select
        v-else-if="isTerminal"
        :modelValue="terminalGuid"
        :location="selectedLocation"
        :disabled="disabled || isFake"
        :purpose="terminalPurpose"
        :useMap="useMap"
        :terminal-purpose="terminalPurpose"
        :required="required"
        :labelPublicStyle="publicStyle"
        :hasSchemeLink="terminalHasSchemeLink"
        show-switcher
        class="mb-10"
        @map-submit="onMapSubmit"
        @change="onChangeTerminal"
      />

      <pup-autocomplete
        v-else
        :modelValue="pickupPointGuid"
        :location="selectedLocation"
        :disabled="disabled || isFake"
        :clear-value-after-blur="addressClearValueAfterBlur"
        :rules="rules"
        :use-map="useMap"
        :suggestion-off="addressSuggestionOff"
        :terminal-purpose="terminalPurpose"
        :class="{'border-light': !pickupPointGuid}"
        :required="required"
        :labelPublicStyle="publicStyle"
        big
        show-switcher
        class="mb-10"
        @map-submit="onMapSubmit"
        @update:modelValue="setPickupPoint"
      />
    </template>
    <slot />
  </div>
</template>

<script setup lang="ts">
import {PropType} from 'vue';
import VzSwitcher from '~/components/ui-kit/vz-switcher.vue';
import VzRadiogroup from '~/components/ui-kit/vz-radiogroup.vue';
import TerminalSelect from "~/components/autocomplete/terminal-select.vue";
import PupAutocomplete from '~/components/autocomplete/pup-autocomplete.vue';
import CityAutocomplete from '~/components/autocomplete/city-autocomplete.vue';
import AddressAutocomplete from '~/components/autocomplete/address-autocomplete.vue';
import {
  Location,
  OrderDirectionAddress,
  OrderDirectionType, RestrictedItem, SelectItem,
  SwitchItem, TerminalPurpose,
} from '~/types/types';
import {useTerminalStore} from '~/stores/terminal';
import {getAddressByGuid, getLocationByFiasGuid, getRestrictedAddress, searchAddressesByLocation} from '@/api/city';
import {getPageContent} from '~/api/content';
import {asyncComputed} from '@vueuse/core';
import formRules from '~/common/vz-form-rules';
import {useOrderStore} from "~/stores/order";
import {toRefs} from "@vue/reactivity";

const emit = defineEmits([
  'update:locationGuid',
  'update:address',
  'update:direction',
  'update:terminalGuid',
  'update:pickupPointGuid',
  'clear'
])

const props = defineProps({
  // model
  locationGuid: {
    type: String,
    default: '',
  },
  direction: {
    type: String as PropType<OrderDirectionType>,
    default: null,
  },
  address: {
    type: String,
    default: '',
  },
  terminalGuid: {
    type: String,
    default: '',
  },
  pickupPointGuid: {
    type: String,
    default: '',
  },
  // options
  locationSearchOptions: {
    type: Object,
    default: null
  },
  country: {
    type: Array,
    default: undefined
  },
  directionTo: {
    type: Boolean,
    default: false,
  },
  hideSwitcher: {
    type: Boolean,
    default: false,
  },
  isRadioSwitcher: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  disableLocation: {
    type: Boolean,
    default: false,
  },
  addressClearValueAfterBlur: {
    type: Boolean,
    default: true,
  },
  titleCard: {
    type: String,
    default: '',
  },
  selectorTitle: {
    type: String,
    default: undefined,
  },
  isRequestDocs: {
    type: Boolean,
    default: false,
  },
  hasPupCost: {
    type: Boolean,
    default: false,
  },
  cost: {
    type: [String, Number],
    default: '',
  },
  useMap: {
    type: Boolean,
    default: false
  },
  purpose: {
    type: String,
    default: ''
  },
  terminalPurpose: {
    type: String as PropType<''|TerminalPurpose>,
    default: ''
  },
  addressSuggestionOff: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  },
  required: {
    type: Boolean,
    default: false,
  },
  publicStyle: {
    type: Boolean,
    default: false,
  },
  switchTitles: {
    type: Object as PropType<Partial<Record<'terminal'|'address'|'pickupPoint'|string, string>>>,
    default: null
  },
  terminalHasSchemeLink: {
    type: Boolean,
    default: false
  },
})

const {$showToast} = useNuxtApp()
const {terminalSelectByLocation} = useTerminalStore()
// Для vz-map-select-modal
provide('directionTo', props.directionTo)
const {dispatchLocation, destinationLocation, isLoadingFullPrice} = toRefs(useOrderStore())

const cityAutocomplete = ref()
const oldLocationGuid = ref('')
const addressIsChanged = ref(false)
const rclLocation = ref<Location|null>()
const restrictedItems = ref<RestrictedItem[]>([])
const selectedLocation = computed((): Location|null => cityAutocomplete.value ? cityAutocomplete.value.selected : null)

const hasTerminals = computed(() => {
  return !!(+(selectedLocation.value?.has_terminals || 0))
})

const addressDisabled = computed(() => {
  return selectedLocation.value?.terminal_only === '1'
})

const terminalList = computed(() => {
  return terminalSelectByLocation(props.locationGuid, props.terminalPurpose)
})

const isAddress = computed(() => {
  return props.direction === 'address'
})

const isTerminal = computed(() => {
  return props.direction === 'terminal'
})

const isFake = computed(() => {
  return Number(selectedLocation.value?.fake) === 1
})

const rules = computed(() => {
  return props.clearable ? [] : [formRules.required]
})

const popoverDescription = computed(() => {
  if (!rclLocation.value?.name) {
    return `Не указан населенный пункт. Доставка осуществляется ${props.directionTo ? 'до' : 'от'} адреса.`
  }
  return `Доставка осуществляется
    ${props.directionTo ? 'до' : 'от'} адреса.
     Ближайший терминал в г. ${rclLocation.value.name}`
})

const thisLocation = computed(() => {
  return cityAutocomplete.value?.selected?.guid ? cityAutocomplete.value.selected : (props.directionTo ? destinationLocation.value : dispatchLocation.value)
})

const pickupPointPopover = asyncComputed(async () => {
  if (!props.useMap) {
    // В модалке выбора на карте. Не отображаем
    return ''
  }
  const content = (await getPageContent('/personal/order/create')).blocks
  return content?.pickupPointInfo.hint
}, undefined, { lazy: true })

const directions = computed(() => {
  const result: SwitchItem[] = [
    {
      title: `${thisLocation.value?.restricted ? 'КПП' : 'Адрес'}${ addressDisabled.value ? '' : props.cost }`,
      value: 'address',
      disabled: addressDisabled.value,
      disabledText: ''
    },
    {
      title: 'Терминал',
      value: 'terminal',
      disabled: !!selectedLocation.value && !hasTerminals.value,
      disabledText: popoverDescription.value
    }
  ]

  if (props.directionTo && ((selectedLocation.value?.pup_count !== '0' && selectedLocation.value?.pup_count) || !props.useMap)) {
    result.push({
      title: `Пункт${pupCost.value}`,
      icon: {
        url: '/svg/logo/yandex.svg',
        variant: 'default',
        size: 16
      },
      classItem: props.switchTitles?.['pickupPoint'] ? '' : 'pvz-yandex',
      value: 'pickupPoint',
      tooltipText: pickupPointPopover.value,
      tooltipPlacement: 'right',
      tooltipIconOptions: { name: 'information-outline', size: 14, variant: 'primary', class: 'mr-5' },
      disabled: (selectedLocation.value?.pup_count === '0' || !selectedLocation.value?.pup_count),
      disabledText: 'Отсутствуют пункты выдачи'
    })
  }

  if (props.switchTitles) {
    const keys = Object.keys(props.switchTitles)
    return result
      .filter(item => keys.includes(item.value))
      .map(item => ({...item, title: props.switchTitles[item.value]}))
  }

  return result
})

const directionType = computed(() => {
  return props.directionTo ? 'destination' : 'dispatch'
})

const directionName  = computed(() => {
  return props.isRequestDocs ? 'сопроводительных документов' : props.directionTo ? 'прибытия' : 'отправления'
})

const computedLocationSearchOptions = computed(() => {
  return props.locationSearchOptions || { country: props.country, purpose: props.purpose }
})

const pupCost = computed(() => {
  return props.hasPupCost && selectedLocation.value?.pup_price ? ` (${selectedLocation.value?.pup_price} ₽)` : ''
})

function setLocation(location: Location): void {
  const locationGuid = location?.guid || ''
  setLocationGuid(locationGuid, location)
}

function setLocationGuid(value: string, location?: Location) {
  emit('update:locationGuid', value, location)
}

function setAddress(value: string) {
  emit('update:address', value)
}

function setTerminal(value: string|number|null) {
  emit('update:terminalGuid', value)
}

function setPickupPoint(value: string) {
  emit('update:pickupPointGuid', value)
}

function setDirection(value: OrderDirectionType|null) {
  emit('update:direction', value)
}

function onTerminalListChange() {
  if (isAddress.value) {
    return
  }
  setTerminal(terminalList.value?.[0]?.value || '')
}

async function checkAddress({ value, obj }: { value: string, obj: OrderDirectionAddress }) {
  if (!selectedLocation.value) {
    return value
  }
  // Проверка на смену города
  const checkedAddress = obj?.unfamiliar
    ? obj
    // : (await searchAddressesByLocation(`${obj?.region}, ${value}`, selectedLocation.value, { check: true }))?.[0]?.data
    : (await searchAddressesByLocation(obj?.daDataShame || `${obj?.region}, ${value}`, selectedLocation.value, { check: true }))?.[0]?.data

  const addressFias = checkedAddress?.fias
  const addressDistrictFias = checkedAddress?.district_fias
  let newFias: null|string = ''

  if (addressDistrictFias) {
    if (selectedLocation.value?.fias !== addressDistrictFias) {
      newFias = addressDistrictFias
    }
  } else if (selectedLocation.value.fias !== addressFias) {
    newFias = addressFias
  }

  if (!newFias && !(obj?.locationGuid && obj.locationGuid !== props.locationGuid)) {
    return value
  }

  // смена города
  const data = newFias
    ? await getLocationByFiasGuid(newFias)
    : [(await getAddressByGuid(obj.locationGuid || '')).location]

  if (data?.length && data[0].guid !== props.locationGuid) {
    setLocation(data[0])
  } else if (!data?.length) {
    $showToast({
      autocloseDelay: 15000,
      variant: 'warning',
      title: 'Внимание',
      text: [
        h('div', `По указанному адресу доставки ${obj.region}, ${value} перевозка не осуществляется. Свяжитесь с нами по тел. `),
        h('a', { attrs: { href: 'tel:88007072002' } }, '8 800 707 20 02'),
        h('div', ' или укажите другой адрес')
      ]
    })
  }
}

async function checkLocation(location: Location) {
  if (isTerminal.value) {
    if (!(+location.has_terminals)) {
      await onChangeDirection('address')
    }
    const newCityTerminals = terminalSelectByLocation(location.guid, props.terminalPurpose)
    if (!newCityTerminals.find(t => t.guid === props.terminalGuid)) {
      setTerminal(newCityTerminals[0]?.value)
    }
  }
  if (props.direction === 'pickupPoint' && (location?.pup_count === '0' || !location.pup_count)) {
    await onChangeDirection('address')
  }
  if (!location?.rcl_guid) {
    rclLocation.value = null
  } else {
    // Для вывода текста о недоступности терминала
    rclLocation.value = (await getAddressByGuid(location?.rcl_guid)).location
  }
}

function onMapSubmit(data: { address: string, direction: OrderDirectionType, location: string, terminal: string, pickupPoint: string, selectedLocation?: Location }) {
  setDirection(data.direction)
  setLocationGuid(data.location, data.selectedLocation)
  if (data.direction === 'address') {
    setAddress(data.address)
  } else if (data.direction === 'terminal') {
    setTerminal(data.terminal)
  } else {
    setPickupPoint(data.pickupPoint)
  }
}

async function onChangeLocation(val: string, obj: Location) {
  setAddress('')
  setPickupPoint('')
  setLocation(obj)

  if (!isTerminal.value) {
    setTerminal('')
  }

  if (isAddress.value) {
    if (obj?.terminal_only === '1') {
      await onChangeDirection('terminal')
    }
  } else {
    if (  terminalSelectByLocation(val, props.terminalPurpose).length ) {
      if (isTerminal.value) {
        setTerminal(terminalSelectByLocation(val, props.terminalPurpose)[0]?.value)
      }
    } else {
      await onChangeDirection('address')
    }
  }
  if (!val) {
    setDirection(null)
    setTerminal(null)
  }
}

async function onChangeAddress(value: string, obj: OrderDirectionAddress) {
  oldLocationGuid.value = props.locationGuid
  addressIsChanged.value = true
  // Очистка подсказки
  if (obj?.region && obj.region.includes('<span')) {
    obj.region = ''
  } else {
    // При не кастомном адресе возможна смена города
    await checkAddress({ value, obj })
  }

  setAddress(value)

  emit('update:address', value, obj)
  nextTick( () => {
    addressIsChanged.value = false
  })
}

async function onChangeDirection(val: OrderDirectionType) {
  if (val === props.direction || props.disabled) {
    return
  }
  if (val === 'terminal' && terminalList.value.length && !props.terminalGuid) {
    setTerminal(terminalList.value?.[0]?.value || '')
  }
  setDirection(val)
  await nextTick()
  if (terminalList.value[0]?.location_guid !== props.locationGuid ||
    val === 'terminal' && (!props.terminalGuid || !terminalList.value.find(t => t.guid === props.terminalGuid))) {
    onTerminalListChange()
  }
}

function onChangeTerminal({ value }: SelectItem) {
  setTerminal(value)
}

watch(() => selectedLocation.value,
  () => {
    if (selectedLocation.value) {
      checkLocation(selectedLocation.value)
    }
  },
  {immediate: true}
)

watch(() => props.locationGuid, (n) => {
  if ( n !== oldLocationGuid.value && addressIsChanged.value && props.address) {
    oldLocationGuid.value = n
    $showToast({
      variant: 'warning',
      title: `Был изменен населенный пункт ${directionName.value}`
    })
  }
})

watch(directions,
  () => {
    if (!selectedLocation.value?.guid) {
      return
    }
    if (directions.value[0]?.disabled && props.direction === directions.value[0].value) {
      onChangeDirection(directions.value[1].value)
    } else if (directions.value[1]?.disabled && props.direction === directions.value[1].value) {
      onChangeDirection(directions.value[0].value)
    }
  },
  { immediate: true }
)

watch(() => thisLocation.value?.guid, async () => {
  if(!thisLocation.value?.restricted) {
    restrictedItems.value = []
    return
  }

  const result = await getRestrictedAddress(thisLocation.value.guid)

  if(result?.length > 0) {
    restrictedItems.value = result
  } else {
    restrictedItems.value = []
  }
}, {immediate: true})

defineExpose({
  selectedLocation,
  restrictedItems
})
</script>

<style lang="scss" scoped>
.tiny-switch-items:deep(.vz-switcher-item) {
  padding-left: 10px !important;
  padding-right: 10px !important;
}
</style>

